import styled, { keyframes, css } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS, ANIM, MOBILE, AFFILIATE_FORM, GRADIENTS, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';
import { thinScrollbar, hoverWash, scrollFadeMaskY } from '../../../../theme/mixins';

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

/* The banner being edited, shown for context at the top of the sheet. */
export const CurrentWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CurrentLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

export const CurrentImg = styled.img`
  width: 100%;
  max-height: 168px;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  display: block;
`;

export const FieldLabel = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  margin-bottom: 8px;
`;

/* Premium chat-style composer: attachments + textarea + an attach toolbar in ONE bordered box that
   lights up on focus and on a drag-over. */
export const PromptComposer = styled.div<{ $dragging: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  background: ${({ $dragging }) => ($dragging ? COLORS.surfaceMuted : COLORS.surfaceFaint)};
  border: 1px solid ${({ $dragging }) => ($dragging ? COLORS.accent : COLORS.hairline)};
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:focus-within {
    border-color: ${COLORS.accent};
  }
`;

/* Attachment thumbnails, inline at the top of the composer (like an LLM chat input). */
export const AttachmentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Attachment = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 56px;
  height: 56px;
  border-radius: 9px;
  overflow: hidden;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/* The textarea, now borderless/transparent — the composer box owns the frame. */
export const PromptArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  resize: none;
  padding: 0;
  border: none;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;

  &:focus {
    outline: none;
  }
  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

/* Bottom toolbar of the composer: the attach affordance, then the art style chip and the mascot picker at the
   end (they wrap under it on a narrow sheet). */
export const ComposerToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
`;

/** The art style chip and the mascot picker, on one row under the composer's "Add image" row. */
export const ComposerChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};

  &:empty {
    display: none;
  }
`;

/* ── "From its definition": what the banner's casino-creation definition shows, read-only ─────── */

export const DefinitionPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]};
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
`;

/** The subject and its lines, collapsed to a few lines until "Show more". */
export const DefinitionSubject = styled.div<{ $open: boolean }>`
  font-size: 12px;
  line-height: 17px;
  color: ${COLORS.textMuted};
  white-space: pre-line;
  overflow-wrap: anywhere;
  ${({ $open }) =>
    $open
      ? ''
      : css`
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        `}
`;

export const ShowMoreBtn = styled.button`
  align-self: flex-start;
  appearance: none;
  border: 0;
  padding: 0;
  background: none;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.accent};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${COLORS.heading};
  }
`;

export const DefinitionRefs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

export const DefinitionRef = styled.img`
  display: block;
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
`;

export const AttachBtn = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 9px;
  border: 1px solid ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
    color: ${COLORS.textBright};
  }
  input {
    display: none;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

export const Chip = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.textMuted};
  font-family: inherit;
  font-size: 12.5px;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.textBright};
    border-color: ${COLORS.hoverBorder};
  }
`;

/* Restyle: a grid of banner looks to pick. */
export const Looks = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

export const Look = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border-radius: 12px;
  cursor: pointer;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.accent : COLORS.hairline)};
  text-align: left;
  font-family: inherit;
`;

export const LookThumb = styled.div`
  width: 100%;
  aspect-ratio: 16 / 7;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const LookName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.text};
`;

/* Upload dropzone. */
export const Drop = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 26px 16px;
  border-radius: 12px;
  border: 1px dashed ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.textMuted};
  cursor: pointer;
  text-align: center;

  input {
    display: none;
  }
`;

export const UploadPreview = styled.img`
  width: 100%;
  max-height: 180px;
  object-fit: contain;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};
`;

const shimmer = keyframes`
  0% { background-position: -180% 0; }
  100% { background-position: 180% 0; }
`;

/* Generating state: a wide shimmer band standing in for the incoming artwork. */
export const Generating = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 8px 0;
`;

export const GenBand = styled.div`
  width: 100%;
  aspect-ratio: 16 / 6;
  border-radius: 12px;
  background: linear-gradient(
    100deg,
    ${COLORS.surfaceInset} 30%,
    ${COLORS.surfaceMuted} 50%,
    ${COLORS.surfaceInset} 70%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ${ANIM.EASE} infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PreviewWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const PreviewImg = styled.img`
  width: 100%;
  aspect-ratio: 16 / 6;
  object-fit: cover;
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.accent};
`;

export const ErrorNote = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  font-size: 13px;
  color: ${COLORS.statusDanger};
`;

export const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

/* ── version history (initial + regenerations) ──────────────────────────────── */

/* The large preview of the selected version — shown at its natural aspect (full image, never cropped). */
export const VersionPreview = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  display: block;
`;

/* Relative wrapper so the "Preview <format>" chip can sit over the big preview's corner. */
export const PreviewFrame = styled.div`
  position: relative;
`;

/* Names the crop the big preview is showing (the surface's hero slot), e.g. "Preview 2:1 · right".
   Natural case so the format token matches the ALL FORMATS grid ("2:1 · right"). */
export const PreviewChip = styled(Text).attrs({ tag: 'span' as const })`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${COLORS.textBright};
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

/* Wraps the composer's base-image preview so a small label can sit over its corner. */
export const BasePreview = styled.div`
  position: relative;
`;

/* Small chip on the top-left of the base image, marking it as the version this variant regenerates
   from. Mirrors the on-thumb version tag so it reads consistently on any artwork. */
export const BaseChip = styled(Text).attrs({ tag: 'span' as const })`
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 1;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${COLORS.textBright};
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.hairline};
`;

export const VersionTag = styled.span<{ $tone: 'initial' | 'current' }>`
  position: absolute;
  top: 4px;
  left: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  /* Live tag: the same publish blue as the CTA, with dark ink (legible on the light-cyan light theme);
     the Initial tag stays the neutral card chip. */
  color: ${({ $tone }) => ($tone === 'current' ? COLORS.ctaSolidText : COLORS.textBright)};
  background: ${({ $tone }) => ($tone === 'current' ? COLORS.publishBlue : COLORS.surfaceCard)};
  border: 1px solid ${({ $tone }) => ($tone === 'current' ? COLORS.publishBlue : COLORS.hairline)};
`;

export const VersionActions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

/* ── split editor layout: version list (1/4) · preview + formats (3/4) ────────── */

/* Mobile drill-down transitions: the detail slides in from the right (forward), the list slides
   back in from the left (back). */
const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(7%);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-7%);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
/** Which drill direction a pane is entering on: forward (into detail) or back (to the list). */
export type DrillDir = 'in' | 'out';

/* Desktop: a fixed-height two-pane split — the version list (1/4) and the big preview + actions +
   formats (3/4) — each pane scrolling independently, a hairline dividing them. Mobile: a single
   column that grows with the sheet (the page renders EITHER pane at a time via the drill-down). */
export const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
  /* FILL the stage: the split takes the sheet's stage height (the backdrop's, see Sheet: the viewport, or the
     content card once the backdrop is pinned to it) MINUS the sheet's own chrome — header + body padding +
     backdrop gutter, ~200px with headroom — so the sheet spans the whole stage while the panes own the
     scrolling (no second, outer scrollbar). */
  height: calc(var(--sheet-stage-height, 100dvh) - 200px);
  align-items: stretch;

  ${MOBILE} {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

/* Left pane: a fixed "Versions" label above the scrolling version list. */
export const LeftPane = styled.div<{ $anim?: DrillDir }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  min-height: 0;
  padding-right: 20px;

  ${MOBILE} {
    padding-right: 0;
    animation: ${({ $anim }) =>
      $anim === 'out'
        ? css`
            ${slideInLeft} 260ms ${ANIM.EASE} both
          `
        : 'none'};
  }
  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* The scrolling region of the left pane: the live thumb sticks to its top while the other variants
   scroll beneath it (with a soft fade at the boundary). */
export const VersionScroll = styled.div<{ $fadeBottom?: boolean }>`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Thumbnails as wide as the pinned live thumb above. LeftPane pads 20px on the right; we push this
     scroll box 12px INTO that gutter (negative margin) and pad the content back 6px, so the thin (6px)
     always-present scrollbar lands in the gutter — 6px off the thumbnails, ~8px off the pane divider —
     while the thumbnails line up with the sticky thumb. (No scrollbar-gutter: on Safari it reserves the
     classic width even for a thin bar, which over-insets the thumbnails.) */
  margin-right: -12px;
  padding-right: 6px;
  ${thinScrollbar}
  /* Fade only the BOTTOM edge as more variants sit below (the top is owned by the pinned live thumb,
     which must stay crisp), and only while there is actually hidden content past it. */
  ${({ $fadeBottom }) => scrollFadeMaskY(false, Boolean($fadeBottom))}

  ${MOBILE} {
    overflow: visible;
    min-height: auto;
    padding-right: 0;
  }
`;

/* The live version, pinned to the top of the scroll region on an opaque backing so the other
   variants pass behind it, with a soft fade below its edge as they scroll under. */
export const LiveSticky = styled.div<{ $scrolled?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 2;
  background: ${AFFILIATE_FORM.surface};

  /* The soft fade below the pinned live thumb only appears once the list is actually scrolled (items
     passing under it). At rest it stays off, so the first item — and its selection border — is crisp. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 18px;
    background: ${GRADIENTS.leftPaneScrollFade};
    pointer-events: none;
    opacity: ${({ $scrolled }) => ($scrolled ? 1 : 0)};
    transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }

  ${MOBILE} {
    position: static;

    &::after {
      display: none;
    }
  }
`;

/* Right pane: the big preview, its actions and the formats list — scrolls independently, with a
   hairline dividing it from the version list. */
/* Right pane shell: a scrolling content area above a fixed action footer (Create + Publish). */
export const RightPane = styled.div<{ $anim?: DrillDir }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding-left: 20px;
  border-left: 1px solid ${COLORS.hairline};

  ${MOBILE} {
    min-height: auto;
    padding-left: 0;
    border-left: none;
    animation: ${({ $anim }) =>
      $anim === 'in'
        ? css`
            ${slideInRight} 260ms ${ANIM.EASE} both
          `
        : 'none'};
  }
  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* The scrolling content of the right pane (preview + actions + formats), above the fixed footer. */
export const RightScroll = styled.div<{ $fadeTop?: boolean; $fadeBottom?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Keep the content off the scrollbar; a little breathing room above the footer. */
  padding-right: 12px;
  padding-bottom: 8px;
  ${thinScrollbar}
  /* Fade whichever edge has hidden content past it, so the preview/formats don't look hard-cut at
     the sheet's padding while scrolling. */
  ${({ $fadeTop, $fadeBottom }) => scrollFadeMaskY(Boolean($fadeTop), Boolean($fadeBottom))}

  ${MOBILE} {
    overflow: visible;
    min-height: auto;
    padding-right: 0;
  }
`;

/* Fixed action footer of the right pane: Create + Publish side by side, always visible while the
   content scrolls above it (never overlapping it). */
export const RightFooter = styled.div`
  flex: 0 0 auto;
  padding-top: 12px;
  padding-right: 12px;
  border-top: 1px solid ${COLORS.hairline};
  background: ${AFFILIATE_FORM.surface};

  /* On mobile the whole sheet scrolls, so pin the footer to the bottom of the sheet's scrollport
     (content scrolls under its opaque backing) instead of letting it flow off the end. */
  ${MOBILE} {
    position: sticky;
    bottom: 0;
    z-index: 1;
    padding-right: 0;
    padding-bottom: 12px;
  }
`;

export const FooterRow = styled.div<{ $center?: boolean }>`
  display: flex;
  align-items: stretch;
  /* When "Create new version" is the only control, centre it at its natural width instead of stretching. */
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  gap: 10px;

  /* On mobile the two CTAs stack (full-width) rather than sitting side by side. */
  ${MOBILE} {
    flex-direction: column;
  }
`;

/* One footer CTA (Create / Publish / live-state / generate-formats), sized to the app's 48px control
   height and sharing the row equally. Tone picks the treatment: orange outline (Create), blue fill
   (Publish / Generate), or a disabled blue outline (the live-state marker). */
export const FooterBtn = styled.button<{ $tone: 'primary' | 'outline' | 'ghostBlue'; $fit?: boolean }>`
  ${hoverWash}
  /* $fit: shrink to the label (used when Create is the only control); otherwise share the row evenly. */
  flex: ${({ $fit }) => ($fit ? '0 0 auto' : '1 1 0')};
  min-width: 0;
  display: flex;
  box-sizing: border-box;
  min-height: 48px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 ${({ $fit }) => ($fit ? '28px' : '12px')};
  border-radius: 99px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  ${({ $tone }) =>
    $tone === 'primary'
      ? css`
          /* Light cyan (harmonious across themes) with dark ink — white read poorly on it. */
          background: ${COLORS.publishBlue};
          border: 1px solid ${COLORS.publishBlue};
          color: ${COLORS.ctaSolidText};
        `
      : $tone === 'ghostBlue'
        ? css`
            background: transparent;
            border: 1px solid ${COLORS.accent};
            color: ${COLORS.textMuted};
          `
        : css`
            background: transparent;
            border: 1px solid ${COLORS.ctaBorder};
            color: ${COLORS.ctaText};
          `}

  &:disabled {
    cursor: default;
    opacity: ${({ $tone }) => ($tone === 'ghostBlue' ? 1 : 0.45)};
  }
  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;

/* Label inside a footer button, ellipsised so a long CTA never overflows its half of the row. */
export const FooterBtnLabel = styled(Text).attrs({ tag: 'span' as const })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

/* "More actions" (⋮) kebab + its flyout, anchored at the end of the footer row. */
export const MenuWrap = styled.div`
  position: relative;
  flex: 0 0 auto;
`;

export const KebabBtn = styled.button`
  ${hoverWash}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 48px;
  min-height: 48px;
  border-radius: 99px;
  border: 1px solid ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.text};
  cursor: pointer;
`;

/* Invisible full-viewport layer that closes the flyout on any outside click. */
export const MenuBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5;
`;

/* The flyout opens UPWARD (the footer sits at the bottom), right-aligned to the kebab. */
export const Flyout = styled.div`
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 6;
  min-width: 184px;
  padding: 6px;
  border-radius: 12px;
  background: ${AFFILIATE_FORM.surface};
  border: 1px solid ${COLORS.hairline};
  box-shadow: ${COLORS.menuShadow};
`;

export const FlyoutItem = styled.button<{ $tone?: 'danger' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 9px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${({ $tone }) => ($tone === 'neutral' ? COLORS.text : COLORS.danger)};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.surfaceInset};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/* The publish-confirm modal: a destination field (label + segmented control) above the confirm copy. */
export const ConfirmField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
`;

export const DestLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12.5px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/* Segmented control housing the surface options — full width, options share the row equally. */
export const DestSeg = styled.div`
  display: flex;
  width: 100%;
  gap: 3px;
  padding: 4px;
  border-radius: 99px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const DestOption = styled.button<{ $active: boolean }>`
  appearance: none;
  border: 0;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 650;
  min-height: 34px;
  padding: 0 10px;
  border-radius: 99px;
  white-space: nowrap;
  transition:
    color ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE},
    background ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};
  ${({ $active }) =>
    $active
      ? css`
          /* The live-chip blue with dark ink, matching the Publish CTA. */
          background: ${COLORS.publishBlue};
          color: ${COLORS.ctaSolidText};
        `
      : css`
          background: transparent;
          color: ${COLORS.textMuted};
          &:hover {
            color: ${COLORS.heading};
          }
        `}
`;

/* The confirm modal's primary action — full-width publish-blue button (matching the Publish CTA). */
export const ConfirmPublishBtn = styled.button`
  ${hoverWash}
  width: 100%;
  box-sizing: border-box;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 99px;
  background: ${COLORS.publishBlue};
  border: 1px solid ${COLORS.publishBlue};
  color: ${COLORS.ctaSolidText};
  font-family: inherit;
  font-size: 14px;
  font-weight: 650;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

/* The other variants beneath the live thumb (the live one is pinned separately above). */
export const VersionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* Hairline between the pinned live thumb and the origin-grouped accordion beneath it. */
export const RailDivider = styled.div`
  height: 1px;
  background: ${COLORS.hairline};
  margin: 12px 2px 8px;
`;

/* One origin-surface accordion (Homepage / Promotions) in the version rail. */
export const AccordionSection = styled.div`
  & + & {
    margin-top: 2px;
  }
`;

/* The accordion column inside the version scroll. It grows to at least the viewport (flex: 1 0 auto),
   so the RailSpacer below can push the Archived section to the FOOT of the rail while the versions
   don't fill it — and collapse to a small gap once they do (then everything simply scrolls). */
export const RailInner = styled.div`
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/* Absorbs the free space between the Versions and Archived sections: pins Archived to the foot when the
   versions are short, and collapses to a small gap (moving the Archived header up next to Versions) once
   they fill the rail. */
export const RailSpacer = styled.div`
  flex: 1 1 auto;
  min-height: 12px;
`;

/* Animated collapse/expand for an accordion section's body. The grid 1fr↔0fr trick animates all the way
   to the content's natural height (which max-height can't do cleanly); the inner clips while it moves. */
export const Collapsible = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows ${ANIM.CREATIVE_TAB_SWITCH_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const CollapsibleInner = styled.div`
  overflow: hidden;
  min-height: 0;
`;

export const AccordionHeader = styled.button`
  /* Sticky so the section label stays visible while its thumbnails scroll under it. A FLAT opaque band
     (matching the sheet surface, no rounded corners) so nothing peeks past it; sits below the pinned
     live thumb, which lives above the scroll. */
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 4px;
  border: 0;
  background: ${AFFILIATE_FORM.surface};
  cursor: pointer;
  font-family: inherit;
  transition: color ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.heading};
  }
`;

/* The Archived section's header — a FOOT-anchored accordion header. While COLLAPSED it sticks to the
   BOTTOM of the rail, so it stays visible even when the versions overflow (its sticky container is the
   tall RailInner, which is what gives bottom-stick a range to work in — a header wrapping only itself
   has none). Once EXPANDED it drops back to normal flow, rising to sit right after the Versions section
   with its thumbnails below. Inherits the flat opaque band from AccordionHeader. */
export const ArchivedHead = styled(AccordionHeader)<{ $open?: boolean }>`
  position: ${({ $open }) => ($open ? 'static' : 'sticky')};
  top: auto;
  bottom: 0;
`;

/* The pinned "live" thumb sits above the scroll; keep it at its natural aspect (never let the flex
   column shrink it). */
export const LiveTop = styled.div`
  flex: 0 0 auto;
`;

export const ChevronWrap = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  color: ${COLORS.textMuted};
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform ${ANIM.CREATIVE_CHEVRON_MS}ms ${ANIM.EASE};
`;

export const AccordionTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
`;

export const AccordionCount = styled.span`
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceInset};
  padding: 1px 8px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
`;

export const AccordionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0 2px;
`;

export const AccordionEmpty = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textFaint};
  padding: 2px 8px 6px;
`;

/* One version in the vertical list: a full-width thumbnail with its Live/Initial tag; the selected
   one carries the accent border. */
export const VersionRow = styled.button<{ $selected: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  /* 2px inner gap so the SELECTED border is outset from the thumb, never touching it; unselected
     thumbs show no border at all (transparent), the gap simply reads as part of the surface. */
  padding: 2px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: transparent;
  border: 2px solid ${({ $selected }) => ($selected ? COLORS.ctaBorder : 'transparent')};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? COLORS.ctaBorder : COLORS.hairline)};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }
`;

/* Mobile-only corner affordance on each version thumb: a circular arrow marking the thumb as
   tap-through to its detail view. Sits over the lower-right on a dark chip shade so it stays legible
   over any artwork; the whole row is the tap target, so it never intercepts pointer events itself. */
export const DrillHint = styled.span`
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  color: ${COLORS.textBright};
  background: ${GRADIENTS.chipShade};
  pointer-events: none;
`;

/* Live "generating" row at the top of the list while a regeneration runs — same footprint as a
   version row, the dot-field texture behind a "Generating…" label. */
export const GenRow = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
  border: 1.5px solid ${COLORS.accent};
  background: ${COLORS.surfaceInset};
`;

/* Mobile drill-down header: a back arrow + the banner name, rendered in the Sheet's header slot (the
   Sheet still owns the close button). */
export const BackHeader = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

/* Clean, borderless back arrow (same arrow-left the shared drill-in BackHeader uses). */
export const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: -6px;
  padding: 6px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${COLORS.text};
  cursor: pointer;
  transition:
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.textBright};
    background: ${COLORS.surfaceMuted};
  }
`;

/* Drill-in title, using the same gradient text as the sheet's own header title. */
export const HeaderTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 17px;
  font-weight: 900;
  line-height: 1.15;
  background: ${GRADIENTS.backHeaderTitle};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const MiniBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: 8px;
  cursor: pointer;
  background: ${COLORS.surfaceMuted};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    border-color: ${COLORS.hoverBorderLit};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* Compact icon-only variant of MiniBtn (square), for the per-format download. */
export const IconMiniBtn = styled(MiniBtn)`
  flex: 0 0 auto;
  padding: 7px;
`;

/* "See prompt" toggle, pushed to the right end of the actions row (opposite Download all). */
export const PromptToggleBtn = styled(MiniBtn)`
  margin-left: auto;
`;

/* Version-switch transition: the right-pane content fades + slides in whenever the selected version
   changes (the wrapper is keyed on the version id, so it remounts and replays this). */
const contentIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;
export const VersionView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${contentIn} ${ANIM.CREATIVE_VIEW_IN_MS}ms ${ANIM.EASE} both;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/* The publish-confirm modal's copy (names the destination + what it replaces). */
export const PublishText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  line-height: 18px;
  color: ${COLORS.text};
`;

/* ── reference-image remove affordance (the × on an attachment thumbnail) ────── */

export const RefRemove = styled.button`
  position: absolute;
  top: 3px;
  right: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  cursor: pointer;
  color: ${COLORS.textBright};
  background: ${COLORS.surfaceCard};
`;

export const RefHint = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  margin-top: 8px;
  font-size: 11px;
  line-height: 15px;
  color: ${COLORS.textFaint};
`;

/* ── live "generating" indicator (label centred over the generating row) ─────── */

export const GenThumbLabel = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: grid;
  place-items: center;
  padding: 0 6px;
  font-size: 10.5px;
  font-weight: 600;
  text-align: center;
  color: ${COLORS.textBright};
`;

/* Real per-format progress for the run in flight, full-width just beneath the version strip: a count
   line above the bar. */
export const GenProgress = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const GenProgressCount = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/* ── in-thumb "generating formats" overlay ───────────────────────────────────────
   When a "Generate all formats" run is in flight, the generating status stays IN CONTEXT of the version
   it belongs to: the dot-field texture is laid over THAT thumb (behind a faint wash so the near-white
   dots read over any artwork) and the real per-format progress bar is pinned along the thumb's bottom
   edge, over the dots. No phantom "new version" tile, no detached footer bar. */
export const GenThumbOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 8px;
  overflow: hidden;
  pointer-events: none;
  background: ${COLORS.overlaySoft};
`;

export const GenThumbBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 6px 7px 7px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  /* Bottom-up scrim so the count + bar read over the dots AND the artwork behind them. */
  background: ${GRADIENTS.footerScrim};
`;

export const GenThumbCount = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${COLORS.textBright};
`;

/* A footer-button label carrying a trailing cost (e.g. "· 12 credits"): the ACTION part ellipsises when
   the row is tight, but the COST part NEVER truncates, so owners always see what they will be charged. */
export const FooterBtnCost = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
`;

export const FooterBtnCostAction = styled(Text).attrs({ tag: 'span' as const })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

export const FooterBtnCostAmount = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  white-space: nowrap;
`;

/* The Lumio OUTLINE spark (hollow gradient stroke) — the AI mark, for the "Generate new" action. */
export const SparkIcon = styled.img`
  width: 14px;
  height: 14px;
`;

/* ── provenance of the selected version (how it was made) ────────────────────── */

export const Provenance = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${COLORS.hairline};
`;

/* The label + a copy-the-prompt affordance, side by side. */
export const ProvenanceHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ProvenanceLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

export const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  padding: 3px 9px;
  border-radius: 7px;
  cursor: pointer;
  background: ${COLORS.surfaceMuted};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.textMuted};
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
    color: ${COLORS.textBright};
  }
`;

/* "Drawn with": the art style + mascot chips of a generated version. */
export const DrawnWithRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};
`;

export const DrawnWithChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  max-width: 100%;
  min-width: 0;
  padding: ${SPACE[4]} ${SPACE[12]} ${SPACE[4]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  color: ${COLORS.textMuted};
  font-size: 12px;
  font-weight: 600;

  svg {
    flex: none;
  }
`;

export const DrawnWithName = styled(Text).attrs({ tag: 'span' as const })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${COLORS.textBright};
`;

export const DrawnWithHint = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.textFaint};
  font-weight: 500;
`;

export const ProvenanceText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12.5px;
  line-height: 16px;
  color: ${COLORS.text};
`;

/* Thumbnails of the reference images fed into a regeneration (the owner's uploads) — clickable to
   open full-size in the lightbox below. */
export const ProvenanceRefs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 2px;
`;

export const ProvenanceRef = styled.button`
  width: 48px;
  height: 48px;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

/* The banner this version was regenerated FROM — a small landscape thumbnail (natural aspect, opens
   full-size in the lightbox), shown alongside the brief. */
export const SourceThumb = styled.button`
  display: block;
  width: 160px;
  max-width: 100%;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorder};
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

/* ── reference-image preview (rendered inside a second, stacked Sheet) ───────── */

export const LightboxBody = styled.div`
  display: grid;
  place-items: center;
`;

export const LightboxImg = styled.img`
  max-width: 100%;
  max-height: 68vh;
  object-fit: contain;
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  display: block;
`;

/* ── formats, listed directly in the right pane (one image per aspect ratio) ──── */

/* The formats block: a header row (label + Download all) over the grid or the empty state. */
export const FormatsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* Shown when the selected version has no generated formats yet (a main-only regeneration): a clear
   prompt to generate them, or a plain note when generation isn't available. */
export const FormatsEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  border: 1px dashed ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
`;

export const FormatsEmptyText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12.5px;
  line-height: 16px;
  color: ${COLORS.textMuted};
`;

/* Lead line of the empty state: a globe icon + the publishing prerequisite, emphasised above the body. */
export const FormatsEmptyLead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${COLORS.text};
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;

  svg {
    flex: 0 0 auto;
    margin-top: 1px;
  }
`;

/* The intro row above the format grid: the "kept in sync" note on the left, "Download all" on the
   right. Wraps on narrow widths so the button drops below the note rather than overflowing. */
export const FormatsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const FormatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
`;

export const FormatCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
`;

/* Tile image cap — shared by the real image, the reveal box, and the placeholder so a tile keeps the
   same footprint across its whole lifecycle (placeholder → reveal → crisp) and never jumps size. */
const FORMAT_TILE_MAX_H = '160px';

export const FormatImg = styled.img<{ $ratio: string }>`
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio};
  max-height: ${FORMAT_TILE_MAX_H};
  object-fit: contain;
  background: ${COLORS.surfaceInset};
  display: block;
`;

/* Placeholder (not-yet-generated) format card: a dashed, dimmed twin of a real format card, shown
   before a main-only version's formats exist so the owner sees which aspect ratios are coming. */
export const FormatPlaceholderCard = styled(FormatCard)`
  border-style: dashed;
  background: transparent;
  opacity: 0.7;
`;

/* The placeholder image area, drawn in the slot's true aspect ratio (so wide vs tall formats read
   at a glance) with a muted skeleton fill. position:relative + overflow:hidden so it can host the
   generating dot grid (an absolute canvas) while a run is in flight. */
export const FormatPlaceholderImg = styled.div<{ $ratio: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio};
  max-height: ${FORMAT_TILE_MAX_H};
  overflow: hidden;
  display: grid;
  place-items: center;
  color: ${COLORS.textFaint};
  background: ${COLORS.surfaceInset};
`;

/* ── in-grid format reveal (a freshly generated format emerging from the dot grid) ───────────────
   Mirrors the onboarding logo reveal: a pre-blurred copy fades in BENEATH the dot grid, then the grid
   fades out as the blurred copy cross-fades to a crisp copy (blur lifting, a touch of settle-scale).
   Same aspect box + max-height as the crisp/placeholder image, so the tile never changes size. */
export const FormatReveal = styled.div<{ $ratio: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $ratio }) => $ratio};
  max-height: ${FORMAT_TILE_MAX_H};
  overflow: hidden;
  background: ${COLORS.surfaceInset};
`;

const formatRevealCopy = css`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`;

/* The blurred copy: opacity 0→1 beneath the grid during the hold, then fades out as it deblurs. Blur
   is CONSTANT (never animated) so the reveal stays GPU-smooth — the crossfade does the work. */
export const FormatBlurred = styled.img<{ $shown: boolean; $deblur: boolean }>`
  ${formatRevealCopy};
  filter: blur(24px);
  transform: scale(${({ $deblur }) => ($deblur ? 1 : 1.05)});
  opacity: ${({ $shown, $deblur }) => ($shown && !$deblur ? 1 : 0)};
  transition:
    opacity ${({ $deblur }) => ($deblur ? '620ms' : '820ms')} ease,
    transform 760ms cubic-bezier(0.22, 1, 0.36, 1);
  ${REDUCED_MOTION} {
    transition: none;
    transform: none;
    filter: none;
  }
`;

/* The crisp copy: hidden during the hold, cross-fades in (+ settles scale) as the blurred copy fades out. */
export const FormatSharp = styled.img<{ $deblur: boolean }>`
  ${formatRevealCopy};
  transform: scale(${({ $deblur }) => ($deblur ? 1 : 1.05)});
  opacity: ${({ $deblur }) => ($deblur ? 1 : 0)};
  transition:
    opacity 620ms ease,
    transform 760ms cubic-bezier(0.22, 1, 0.36, 1);
  ${REDUCED_MOTION} {
    transition: none;
    transform: none;
  }
`;

/* The generating dot grid layered over a tile (placeholder while empty, or dissolving as a format
   reveals). The caller passes a theme-aware DotField `fill` (near-white on dark, near-black on light) so
   the dots read on an artwork-free placeholder in both themes; on dark the overlaySoft wash also lifts
   the dots off any artwork behind a reveal. Fades out as the format deblurs, then the caller unmounts it
   (stops the canvas). */
export const FormatGenGrid = styled.div<{ $out: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: ${COLORS.overlaySoft};
  opacity: ${({ $out }) => ($out ? 0 : 1)};
  transition: opacity ${ANIM.CREATIVE_OVERLAY_FADE_MS}ms ${ANIM.EASE};
  ${REDUCED_MOTION} {
    transition: none;
  }
`;

export const FormatFoot = styled.div`
  /* Push the label + Download to the card bottom so every card's button aligns across the row
     (cards stretch to the row height, and short landscape crops would otherwise sit high). */
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
`;

export const FormatLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 700;
  color: ${COLORS.text};
  font-variant-numeric: tabular-nums;
`;

/* A subtle note explaining the per-aspect-ratio cost, shown under the composer. */
export const CostNote = styled(Text).attrs({ tag: 'span' as const })`
  display: block;
  margin-top: 8px;
  font-size: 11px;
  line-height: 15px;
  color: ${COLORS.textFaint};
`;
